import type { CreateParticipantEmbeddableFormRequest } from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import { z, zodHttpUrl, zodNonEmptyString } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { HookedSubmitButton } from '../../components/forms/V3HookedSubmitButton';
import { createModal } from '../../components/generic/Modal';
import { useCreateParticipantEmbeddableFormMutation } from '../../graphql/hooks/useCreateParticipantEmbeddableFormMutation';
import { useAppContext } from '../../hooks/useAppContext';
import { zodResolver } from '../../utils/zod';
import { ParticipantEmbeddableFormForm } from './ParticipantEmbeddableFormForm';

type FormValues = CreateParticipantEmbeddableFormRequest;

const participantEmbeddableFormRequestSchema: z.ZodSchema<CreateParticipantEmbeddableFormRequest> =
  z.object({
    name: zodNonEmptyString('No name found'),
    url: zodHttpUrl('Invalid URL, must start with https or http'),
  });

interface Props {
  participantId: string;
}

export const ParticipantEmbeddableFormCreateModal = createModal<Props>((props) => {
  const { participantId, modal } = props;
  const { visible, hide, remove } = modal;
  const { organization, successToast, errorToast } = useAppContext();

  const initialValues: FormValues = {
    name: '',
    url: '',
  };

  const methods = useForm<FormValues>({
    values: initialValues,
    mode: 'all',
    resolver: zodResolver(participantEmbeddableFormRequestSchema),
  });

  const mutation = useCreateParticipantEmbeddableFormMutation();

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      await mutation
        .mutateAsync({
          organizationId: organization.id,
          participantId,
          request: {
            name: values.name,
            url: values.url,
          },
        })
        .then(() => {
          hide();
          successToast('Embeddable form created successfully');
        })
        .catch((err) => {
          errorToast(`Error creating embeddable form: ${err.message}`);
        });
    },
    [errorToast, hide, mutation, organization.id, participantId, successToast],
  );

  return (
    <ChakraV3.Dialog.Root open={visible} onOpenChange={hide} onExitComplete={remove}>
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <ChakraV3.Dialog.Content>
              <ChakraV3.DialogTitleSubtitle
                title='Add embeddable form'
                subtitle='You can add a new embeddable form for this participant'
              />
              <ChakraV3.Dialog.CloseTrigger />
              <ChakraV3.Dialog.Body>
                <ParticipantEmbeddableFormForm />
              </ChakraV3.Dialog.Body>
              <ChakraV3.Dialog.Footer>
                <ChakraV3.HStack>
                  <ChakraV3.Button onClick={hide} size='sm' variant='subtle'>
                    Close
                  </ChakraV3.Button>
                  <HookedSubmitButton>Add form</HookedSubmitButton>
                </ChakraV3.HStack>
              </ChakraV3.Dialog.Footer>
            </ChakraV3.Dialog.Content>
          </form>
        </FormProvider>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
});
